import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { X, Pencil, Trash2, Calendar, Bell, Tag, Users, Building2, CheckSquare, Plus, Trash, Edit2, MessageSquare } from "lucide-react";
import type { Task, TaskColumn, TaskTeam, Subtask, TaskUserSearchResult } from "@/features/tasks/types/task";
import { useTaskDetail } from "@/features/tasks/hooks/useTaskDetail";
import { TaskComments } from "@/features/tasks/components/TaskComments";
import { TaskAttachments } from "@/features/tasks/components/TaskAttachments";
import { TaskStatusBadge } from "@/features/tasks/components/TaskStatusBadge";
import { CategoryBadge } from "@/features/tasks/components/CategoryBadge";
import { SubtaskFormModal } from "@/features/tasks/components/SubtaskFormModal";
import { SubtaskMemberPicker } from "@/features/tasks/components/SubtaskMemberPicker";
import { TaskResponsesTab } from "@/features/tasks/components/TaskResponsesTab";
import { ProfileAvatar } from "@/shared/components/ui/Avatar";
import { formatDisplayName } from "@/shared/utils/formatters";
import { Button } from "@/shared/components/ui/Button";
import { Spinner } from "@/shared/components/ui/Spinner";
import { useFormatDate } from "@/shared/hooks/useFormatDate";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { deleteTask as deleteTaskApi, updateTask } from "@/features/tasks/services/taskService";

type TabId = "info" | "comments" | "files" | "responses";

interface TaskDetailPanelProps {
  task: Task | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (task: Task) => void;
  onDelete: (taskId: string) => void;
  onRefresh: () => void;
  onError?: (err: any) => void;
  columns: TaskColumn[];
  teams: TaskTeam[];
}

const isPrivileged = (userType?: string) =>
  ["officer", "admin"].includes((userType ?? "").toLowerCase());

export const TaskDetailPanel: React.FC<TaskDetailPanelProps> = ({
  task,
  isOpen,
  onClose,
  onEdit,
  onDelete,
  onRefresh,
  onError,
  columns,
  teams,
}) => {
  const { t } = useTranslation();
  const { fmtDate, fmtDateTime } = useFormatDate();
  const { userData } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>("info");
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showSubtaskForm, setShowSubtaskForm] = useState(false);
  const [editingSubtask, setEditingSubtask] = useState<Subtask | null>(null);
  const [subtaskDeletingId, setSubtaskDeletingId] = useState<string | null>(null);
  // Shared member list — all new subtasks are assigned to these members
  const [sharedMembers, setSharedMembers] = useState<TaskUserSearchResult[]>([]);
  const [savingMembers, setSavingMembers] = useState(false);
  const [responseCount, setResponseCount] = useState(0);

  const {
    task: detailTask,
    comments,
    attachments,
    loadingTask,
    loadingComments,
    loadingAttachments,
    uploading,
    fetchTaskDetail,
    addComment,
    deleteComment,
    uploadAttachment,
    deleteAttachment,
    getAttachmentUrl,
  } = useTaskDetail();

  useEffect(() => {
    if (task && isOpen) {
      fetchTaskDetail(task.id);
      setActiveTab("info");
      setConfirmDelete(false);
    }
  }, [task?.id, isOpen]);

  // Load shared members from localStorage when a task is opened, falling back to
  // the first subtask's assignees (e.g. a chat task that auto-created one).
  useEffect(() => {
    if (!detailTask) return;
    const storageKey = `task_members_${detailTask.id}`;
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        setSharedMembers(JSON.parse(saved));
        return;
      } catch {
        localStorage.removeItem(storageKey);
      }
    }
    // Fallback: seed from first subtask's assignees
    const firstAssignees = detailTask.subtasks?.[0]?.assignees;
    if (firstAssignees?.length) {
      const mapped = firstAssignees.map((a) => ({
        user_id:        a.user_id,
        first_name:     a.first_name,
        middle_name:    a.middle_name,
        last_name:      a.last_name,
        profile_pic_id: a.profile_pic_id,
        user_type:      "",
      }));
      setSharedMembers(mapped);
      localStorage.setItem(storageKey, JSON.stringify(mapped));
    } else {
      setSharedMembers([]);
    }
  }, [detailTask?.id]);

  const displayTask = detailTask ?? task;

  // Update local state + localStorage when picker changes (no DB write yet).
  const handleMembersChange = useCallback(
    (members: TaskUserSearchResult[]) => {
      setSharedMembers(members);
      if (displayTask) {
        localStorage.setItem(`task_members_${displayTask.id}`, JSON.stringify(members));
      }
    },
    [displayTask?.id],
  );

  // Persist member list to all existing subtasks in the DB.
  const handleSaveMembers = useCallback(async () => {
    if (!displayTask) return;
    setSavingMembers(true);
    try {
      const memberIds = [...new Set(sharedMembers.map((m) => m.user_id))];
      const existingSubtasks = detailTask?.subtasks ?? [];
      await Promise.all(
        existingSubtasks.map((st) =>
          updateTask(st.id, { assignee_ids: memberIds }),
        ),
      );
      await fetchTaskDetail(displayTask.id);
    } catch (err) {
      console.error("Failed to save subtask members", err);
    } finally {
      setSavingMembers(false);
    }
  }, [displayTask?.id, sharedMembers, detailTask?.subtasks, fetchTaskDetail]);

  const column = columns.find((c) => c.id === displayTask?.column_id);
  const team = teams.find((t) => t.id === displayTask?.team_id);
  const canManage = isPrivileged(userData?.userType);

  const subtasks: Subtask[] = displayTask?.subtasks ?? [];
  const subtaskTotal = subtasks.length;
  const totalSubtaskAssignments = subtasks.reduce(
    (total, subtask) => total + subtask.assignees.length,
    0,
  );
  const completedSubtaskAssignments = subtasks.reduce((total, subtask) => {
    const doneIds = new Set(subtask.completed_by_ids ?? []);
    return total + subtask.assignees.filter((assignee) => doneIds.has(assignee.user_id)).length;
  }, 0);
  const completionPct = totalSubtaskAssignments > 0
    ? Math.round((completedSubtaskAssignments / totalSubtaskAssignments) * 100)
    : null;

  const handleDelete = async () => {
    if (!displayTask) return;
    setDeleting(true);
    try {
      await onDelete(displayTask.id);
      onClose();
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleDeleteSubtask = async (subtaskId: string) => {
    setSubtaskDeletingId(subtaskId);
    try {
      await deleteTaskApi(subtaskId);
      await fetchTaskDetail(displayTask!.id);
      onRefresh();
    } catch (err) {
      console.error("Failed to delete subtask", err);
    } finally {
      setSubtaskDeletingId(null);
    }
  };

  const tabs: { id: TabId; label: string; count?: number }[] = [
    { id: "info", label: t("tasks.detail.info") },
    { id: "comments", label: t("tasks.detail.comments"), count: comments.length },
    { id: "files", label: t("tasks.detail.files"), count: attachments.length },
    { id: "responses", label: t("tasks.responses.tab_label"), count: responseCount || undefined },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full bg-white shadow-2xl z-50 flex flex-col transition-all duration-300 ease-in-out ${
          activeTab === "responses"
            ? "max-w-lg xl:max-w-2xl"
            : "max-w-lg xl:max-w-xl"
        } ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b border-gray-200 gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 line-clamp-2 leading-snug">
              {displayTask?.title ?? t("tasks.detail.title")}
            </h2>
            {column && (
              <div className="mt-1">
                <TaskStatusBadge column={column} />
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? "text-indigo-600 border-b-2 border-indigo-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <span className="flex items-center justify-center gap-1.5">
                {tab.label}
                {tab.count !== undefined && tab.count > 0 && (
                  <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full leading-none ${
                    activeTab === tab.id
                      ? "bg-indigo-100 text-indigo-600"
                      : "bg-gray-100 text-gray-500"
                  }`}>
                    {tab.count}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loadingTask ? (
            <div className="flex justify-center py-12">
              <Spinner />
            </div>
          ) : !displayTask ? null : (
            <>
              {/* INFO TAB */}
              {activeTab === "info" && (
                <div className="flex flex-col gap-4">
                  {/* Description */}
                  {displayTask.description && (
                    <div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {displayTask.description}
                      </p>
                    </div>
                  )}

                  {/* Meta fields */}
                  <div className="flex flex-col gap-3">
                    {/* Category */}
                    <div className="flex items-start gap-3">
                      <Tag className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-gray-400 font-medium mb-0.5">
                          {t("tasks.detail.category")}
                        </p>
                        {displayTask.category ? (
                          <CategoryBadge category={displayTask.category} />
                        ) : (
                          <p className="text-sm text-gray-500">
                            {t("tasks.detail.no_category")}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Deadline */}
                    <div className="flex items-start gap-3">
                      <Calendar className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-gray-400 font-medium mb-0.5">
                          {t("tasks.detail.deadline")}
                        </p>
                        <p className="text-sm text-gray-700">
                          {displayTask.deadline
                            ? fmtDate(displayTask.deadline)
                            : t("tasks.detail.no_deadline")}
                        </p>
                      </div>
                    </div>

                    {/* Reminder */}
                    <div className="flex items-start gap-3">
                      <Bell className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-gray-400 font-medium mb-0.5">
                          {t("tasks.detail.reminder")}
                        </p>
                        <p className="text-sm text-gray-700">
                          {displayTask.remind_at
                            ? fmtDateTime(displayTask.remind_at)
                            : t("tasks.detail.no_reminder")}
                        </p>
                      </div>
                    </div>

                    {/* Assignees */}
                    <div className="flex items-start gap-3">
                      <Users className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-400 font-medium mb-1">
                          {t("tasks.detail.assignees")}
                        </p>
                        {displayTask.assignees.length === 0 ? (
                          <p className="text-sm text-gray-500">
                            {t("tasks.detail.no_assignees")}
                          </p>
                        ) : (
                          <div className="flex flex-col gap-1.5">
                            {displayTask.assignees.map((a) => (
                              <div key={a.user_id} className="flex items-center gap-2">
                                <ProfileAvatar
                                  picId={a.profile_pic_id}
                                  firstName={a.first_name}
                                  lastName={a.last_name}
                                  size="sm"
                                />
                                <span className="text-sm text-gray-700">
                                  {formatDisplayName(a.last_name, a.first_name, a.middle_name)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Team */}
                    {team && (
                      <div className="flex items-start gap-3">
                        <Building2 className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-xs text-gray-400 font-medium mb-0.5">
                            {t("tasks.detail.team")}
                          </p>
                          <p className="text-sm text-gray-700">{team.name}</p>
                        </div>
                      </div>
                    )}

                    {/* Created */}
                    <div className="pt-2 border-t border-gray-100">
                      <p className="text-xs text-gray-400">
                        {t("tasks.detail.created_at")}:{" "}
                        {fmtDateTime(displayTask.created_at)}
                      </p>
                    </div>
                  </div>

                  {/* ── Divider with task actions ────────────────────────────── */}
                  <div className="flex items-center gap-3 py-1">
                    <div className="flex-1 h-px bg-gray-200" />
                    {canManage && (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => onEdit(displayTask)}
                          className="flex items-center gap-1.5"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          {t("tasks.detail.edit")}
                        </Button>

                        {confirmDelete ? (
                          <div className="flex items-center gap-1.5">
                            <Button
                              variant="danger"
                              size="sm"
                              loading={deleting}
                              onClick={handleDelete}
                            >
                              {deleting
                                ? t("tasks.detail.deleting")
                                : t("common.confirm", { defaultValue: "Confirm" })}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setConfirmDelete(false)}
                            >
                              {t("tasks.form.cancel")}
                            </Button>
                          </div>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setConfirmDelete(true)}
                            className="flex items-center gap-1.5 text-red-500 hover:bg-red-50"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            {t("tasks.detail.delete")}
                          </Button>
                        )}
                      </div>
                    )}
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>

                  {/* ── Sub-tasks section ───────────────────────────────────── */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <CheckSquare className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-semibold text-gray-700">
                          {t("tasks.subtasks.title")}
                        </span>
                        {subtaskTotal > 0 && (
                          <span className="text-xs text-gray-400 font-medium">
                            {subtaskTotal}
                          </span>
                        )}
                      </div>
                      {canManage && (
                        <button
                          type="button"
                          onClick={() => setShowSubtaskForm(true)}
                          className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          {t("tasks.subtasks.add_subtask")}
                        </button>
                      )}
                    </div>

                    {/* Shared member list — selected once, reused for all new subtasks */}
                    {canManage && (
                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <p className="text-xs font-medium text-gray-500">
                            {t("tasks.subtasks.member_list")}
                          </p>
                          {subtaskTotal > 0 && (
                            <Button
                              type="button"
                              size="sm"
                              loading={savingMembers}
                              onClick={handleSaveMembers}
                              disabled={sharedMembers.length === 0}
                            >
                              {savingMembers ? t("tasks.form.saving") : t("tasks.subtasks.save_members")}
                            </Button>
                          )}
                        </div>
                        <SubtaskMemberPicker
                          value={sharedMembers}
                          onChange={handleMembersChange}
                        />
                      </div>
                    )}

                    {/* Progress bar */}
                    {totalSubtaskAssignments > 0 && (
                      <div className="mb-3">
                        <div className="flex justify-between text-xs text-gray-400 mb-1">
                          <span>{t("tasks.subtasks.completion")}</span>
                          <span className="font-semibold text-gray-600">
                            {completionPct}% ({completedSubtaskAssignments}/{totalSubtaskAssignments})
                          </span>
                        </div>
                        <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-emerald-500 rounded-full transition-all duration-300"
                            style={{ width: `${completionPct}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Subtask list */}
                    {subtasks.length === 0 ? (
                      <p className="text-xs text-gray-400 italic">
                        {t("tasks.subtasks.no_subtasks")}
                      </p>
                    ) : (
                      <div className="flex flex-col gap-1.5">
                        {subtasks.map((st) => {
                          const isDeleting   = subtaskDeletingId === st.id;
                          const doneIds      = new Set(st.completed_by_ids ?? []);
                          const responseIds  = new Set(st.responded_by_ids ?? []);
                          const completedIds = [...doneIds];
                          const isFullyDone  = st.assignees.length > 0 && doneIds.size >= st.assignees.length;
                          const respondedCount = [...responseIds].filter((id) => !doneIds.has(id)).length;

                          return (
                            <div
                              key={st.id}
                              className={`flex items-start gap-2.5 p-2.5 rounded-lg border transition-colors ${
                                isFullyDone
                                  ? "bg-emerald-50 border-emerald-100"
                                  : "bg-gray-50 border-gray-100"
                              }`}
                            >
                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium leading-snug ${
                                  isFullyDone ? "text-emerald-700" : "text-gray-800"
                                }`}>
                                  {st.title}
                                </p>
                                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                  {st.deadline && (
                                    <span className="text-xs text-gray-400 flex items-center gap-0.5">
                                      <Calendar className="w-3 h-3" />
                                      {fmtDate(st.deadline)}
                                    </span>
                                  )}
                                  {/* Who has completed */}
                                  {completedIds.length > 0 && (
                                    <span className="flex items-center gap-1">
                                      {st.assignees
                                        .filter((a) => completedIds.includes(a.user_id))
                                        .map((a) => (
                                          <ProfileAvatar
                                            key={a.user_id}
                                            picId={a.profile_pic_id}
                                            firstName={a.first_name}
                                            lastName={a.last_name}
                                            size="sm"
                                          />
                                        ))}
                                      {isFullyDone && (
                                        <span className="text-xs text-emerald-600 font-medium">
                                          {t("tasks.subtasks.done")}
                                        </span>
                                      )}
                                    </span>
                                  )}
                                  {respondedCount > 0 && (
                                    <span className="text-xs text-amber-600 flex items-center gap-0.5">
                                      <MessageSquare className="w-3 h-3" />
                                      {t("tasks.responses.response_added", {
                                        defaultValue: "Response added",
                                      })}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Edit */}
                              {canManage && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingSubtask(st);
                                    setShowSubtaskForm(true);
                                  }}
                                  className="shrink-0 text-gray-300 hover:text-indigo-500 transition-colors"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                              )}

                              {/* Delete */}
                              {canManage && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteSubtask(st.id)}
                                  disabled={isDeleting}
                                  className="shrink-0 text-gray-300 hover:text-red-400 transition-colors disabled:opacity-40"
                                >
                                  {isDeleting ? <Spinner size="sm" /> : <Trash className="w-3.5 h-3.5" />}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {/* ── end subtasks ─────────────────────────────────────────── */}
                </div>
              )}

              {/* COMMENTS TAB */}
              {activeTab === "comments" && (
                <TaskComments
                  comments={comments}
                  loading={loadingComments}
                  onAddComment={async (text) => {
                    await addComment(text);
                    onRefresh();
                  }}
                  onDeleteComment={deleteComment}
                />
              )}

              {/* FILES TAB */}
              {activeTab === "files" && (
                <TaskAttachments
                  attachments={attachments}
                  loading={loadingAttachments}
                  uploading={uploading}
                  onUpload={async (file) => {
                    await uploadAttachment(file);
                    onRefresh();
                  }}
                  onDelete={deleteAttachment}
                  onGetUrl={getAttachmentUrl}
                />
              )}

              {/* RESPONSES TAB */}
              {activeTab === "responses" && (
                <TaskResponsesTab
                  taskId={displayTask.id}
                  isActive={activeTab === "responses"}
                  onCountChange={setResponseCount}
                  onRefresh={async () => {
                    await fetchTaskDetail(displayTask.id);
                    onRefresh();
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Subtask form modal */}
      {displayTask && (
        <SubtaskFormModal
          isOpen={showSubtaskForm}
          onClose={() => {
            setShowSubtaskForm(false);
            setEditingSubtask(null);
          }}
          onSaved={async () => {
            await fetchTaskDetail(displayTask.id);
            onRefresh();
          }}
          onError={onError}
          parentTask={displayTask}
          subtask={editingSubtask}
          fixedAssignees={editingSubtask ? undefined : sharedMembers}
        />
      )}
    </>
  );
};
